'use client'
import * as React from 'react'
import { isHotkey } from 'is-hotkey'
import {
  StackItem,
  UndoManagerParams,
  createUndoManager,
  JSONValue,
} from '~/lib/undo-manager'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { BlockBatchMutationParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/batch'
import {
  UpdateBlockParams,
  CreateBlockParams,
  DeleteBlockParams,
  DeleteBlocksParams,
  DuplicateBlockParams,
  UpdateApiNameParams,
  UpdateSlugParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { ChangeBlockTypeParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/change'
import { ReorderBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/reorder'
import {
  RepoStore,
  useRepoStoreRoot,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import {
  Tab,
  getTabKey,
  parseTab,
  useTabsStoreRoot,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { sleep } from '~/lib/sleep'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { CreateAndReferenceBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/reference'
import { SelectOptionOpParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/select'
import { ToggleMakeComponentParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/component'
import { PasteBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/paste'
import {
  ChangeListTemplateParams,
  CreateListViewParams,
  DeleteListViewParams,
  ResetListTemplateParams,
  UpdateListViewParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/list'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import {
  isBranch,
  isReferenceBlock,
  isRichTextBlock,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import {
  expandedStateStorage,
  getExpandedStateKey,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/document/nested/link'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { ApplySuggestionInput } from '@basehub/data/replicache/mutators/repo-space/apply-suggestion/mutators'
import { useYjsStore } from '~/lib/tiptap/components/yjs-store'

const DISABLED = false

export const repoUndoManagerStorage = 'sessionStorage' as const

export type RepoUndoOps = (
  | { op: 'skip'; args: Record<string, never> }
  | { op: 'updateBlock'; args: UpdateBlockParams }
  | { op: 'createBlock'; args: CreateBlockParams }
  | { op: 'deleteBlock'; args: DeleteBlockParams }
  | { op: 'deleteBlocks'; args: DeleteBlocksParams }
  | { op: 'blockBatchMutations'; args: BlockBatchMutationParams }
  | { op: 'changeBlockType'; args: ChangeBlockTypeParams }
  | { op: 'reorderBlocks'; args: ReorderBlockParams }
  | { op: 'duplicateBlock'; args: DuplicateBlockParams }
  | { op: 'createAndReferenceBlock'; args: CreateAndReferenceBlockParams }
  | { op: 'updateApiName'; args: UpdateApiNameParams }
  | { op: 'updateSlug'; args: UpdateSlugParams }
  | { op: 'selectOptionOp'; args: SelectOptionOpParams }
  | { op: 'toggleMakeComponent'; args: ToggleMakeComponentParams }
  | { op: 'pasteBlock'; args: PasteBlockParams }
  | { op: 'createListView'; args: CreateListViewParams }
  | { op: 'updateListView'; args: UpdateListViewParams }
  | { op: 'deleteListView'; args: DeleteListViewParams }
  | { op: 'resetListTemplate'; args: ResetListTemplateParams }
  | { op: 'changeListTemplate'; args: ChangeListTemplateParams }
  | { op: 'applySuggestion'; args: ApplySuggestionInput }
  | { op: 'customWindowEvent'; args: { name: string; detail?: JSONValue } }
  | {
      op: 'focus'
      args: {
        focus: NonNullable<RepoStore['focus']>
        preferredTabKey: string | undefined | null
        forcePreferredTabKey?: boolean
      }
    }
  | { op: 'yjs'; args: { id: string; dir: 'undo' | 'redo' } }
)[]
export type RepoUndoStackItem = StackItem<RepoUndoOps>
export type RepoUndoManager = ReturnType<typeof createUndoManager<RepoUndoOps>>

const RepoUndoManagerContext = React.createContext<
  React.MutableRefObject<RepoUndoManager | null> | undefined
>(undefined)

export const useRepoUndoManager = () => {
  const context = React.useContext(RepoUndoManagerContext)
  if (!context) {
    throw new Error(
      'useRepoUndoManager must be used within a RepoUndoManagerProvider'
    )
  }
  return context
}

export const RepoUndoManagerProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const undoManagerRef = React.useRef<ReturnType<
    typeof createUndoManager<RepoUndoOps>
  > | null>(null)

  const { data } = useRepoReplicache()
  const repoStoreRoot = useRepoStoreRoot()
  const tabsStoreRoot = useTabsStoreRoot()
  const { branchOrCommit } = useBranchOrCommitSegment()
  const branchId =
    branchOrCommit && isBranch(branchOrCommit) ? branchOrCommit.id : undefined
  const treeRef = useWorkingTreeStore((s) => s.treeRef)

  const processStackItem: UndoManagerParams<RepoUndoOps>['processStackItem'] =
    React.useCallback(
      async (intent, stackItem) => {
        const ops = stackItem[intent]

        for (let i = 0; i < ops.length; i++) {
          const opData = ops[i]
          if (!opData) continue
          const { op, args } = opData
          const isLastOp = i === ops.length - 1
          switch (op) {
            case 'skip':
              break
            case 'yjs': {
              const { docs } = useYjsStore.getState()
              const entry = docs.get(args.id)
              if (!entry) break
              args.dir === 'undo'
                ? entry.undoManager.undo()
                : entry.undoManager.redo()
              break
            }
            case 'blockBatchMutations':
              await data?.replicache.mutate.blockBatchMutations(args)
              break
            case 'createBlock':
              await data?.replicache.mutate.createBlock(args)
              break
            case 'updateBlock':
              await data?.replicache.mutate.updateBlock(args)
              break
            case 'deleteBlock':
              await data?.replicache.mutate.deleteBlock(args)
              break
            case 'updateListView':
              await data?.replicache.mutate.updateListView(args)
              break
            case 'applySuggestion':
              await data?.replicache.mutate.applySuggestion(args)
              break
            case 'deleteBlocks':
              await data?.replicache.mutate.deleteBlocks(args)
              break
            case 'changeBlockType':
              await data?.replicache.mutate.changeBlockType(args)
              break
            case 'reorderBlocks':
              await data?.replicache.mutate.reorderBlocks(args)
              break
            case 'duplicateBlock':
              await data?.replicache.mutate.duplicateBlock(args)
              break
            case 'createAndReferenceBlock':
              await data?.replicache.mutate.createAndReferenceBlock(args)
              break
            case 'updateApiName':
              await data?.replicache.mutate.updateApiName(args)
              break
            case 'updateSlug':
              await data?.replicache.mutate.updateSlug(args)
              break
            case 'selectOptionOp':
              await data?.replicache.mutate.selectOptionOp(args)
              break
            case 'deleteListView':
              await data?.replicache.mutate.deleteListView(args)
              break
            case 'resetListTemplate':
              await data?.replicache.mutate.resetListTemplate(args)
              break
            case 'changeListTemplate':
              await data?.replicache.mutate.changeListTemplate(args)
              break
            case 'toggleMakeComponent':
              await data?.replicache.mutate.toggleMakeComponent(args)
              break
            case 'pasteBlock':
              await data?.replicache.mutate.pasteBlock(args)
              break
            case 'customWindowEvent':
              window.dispatchEvent(
                new CustomEvent(args.name, { detail: args.detail })
              )
              await sleep(16)
              break
            case 'focus': {
              let shouldReplay = false

              const repoStore = repoStoreRoot.getState()
              const tabsStore = tabsStoreRoot.getState()

              const tm = treeRef.current

              invariant(tm, 'treeRef.current is required')

              const node = tm.getNode(args.focus.blockId, {
                required: true,
              })
              const path = tm.getPath(node, {
                required: true,
                ignoreSelf: true,
                format: 'array',
              })
              const preferredTabKeyParsed = parseTab({
                key: args.preferredTabKey ?? idToSourceBlockId(node.value),
                partiallyOpen: false,
              })
              invariant(
                preferredTabKeyParsed.type === 'block',
                `only block tab types are supported for undo/redo`
              )
              const preferredTabNode = tm.getNode(
                preferredTabKeyParsed.blockId,
                { required: true }
              )

              const newTabKey = getTabKey({
                blockId: preferredTabNode.value.id,
                type: 'block',
              })

              const validExistingTabs = tabsStore.tabs.filter((t) => {
                const parsed = parseTab(t)
                if (parsed.type !== 'block') return false
                if (
                  !args.forcePreferredTabKey &&
                  parsed.blockId === idToSourceBlockId(node.value)
                ) {
                  return true
                }
                if (parsed.blockId === preferredTabKeyParsed.blockId) {
                  // we also need to check the block will be focusable in this tab
                  const index = path.findIndex((id) => {
                    const block = tm.getNode(id, { required: true }).value
                    return (
                      parsed.blockId === idToSourceBlockId(block) ||
                      parsed.blockId === block.id
                    )
                  })
                  if (index === -1 || index === path.length - 1) {
                    // if the block is a direct child, then yes
                    // or if the block is not a child, then also yes (e.g. sidebar action)
                    return true
                  } else {
                    // else it needs to be "expanded"
                    // within a nested block
                    const trailThatNeedsToBeExpanded = path.slice(index + 1)
                    const allExpanded = trailThatNeedsToBeExpanded.every(
                      (blockId) => {
                        const block = tm.getNode(blockId, {
                          required: true,
                        }).value
                        if (isReferenceBlock(block) || isRichTextBlock(block)) {
                          return true
                        }
                        const key = getExpandedStateKey(block)
                        try {
                          return (
                            JSON.parse(
                              window[expandedStateStorage].getItem(key) ??
                                'false'
                            ) === true
                          )
                        } catch (e) {
                          return false
                        }
                      }
                    )
                    if (allExpanded) return true
                  }
                }
                return false
              })

              const bestTab = validExistingTabs.reduce<Tab | undefined>(
                (best, curr) => {
                  if (tabsStore.activeTab?.key === curr.key) return curr // is active
                  if (curr.key.startsWith(newTabKey)) return curr // is preferred
                  if (!best) return curr // no best yet
                  return best
                },
                undefined
              )

              if (!bestTab) {
                shouldReplay = true
                if (validExistingTabs[0]) {
                  tabsStore.selectTab({
                    ...validExistingTabs[0],
                    partiallyOpen: false,
                  })
                } else {
                  tabsStore.addTab({
                    key: newTabKey,
                    partiallyOpen: true,
                  })
                }
                await sleep(16)
              } else if (bestTab.key !== tabsStore.activeTab?.key) {
                // it's not active, switch to it
                shouldReplay = true
                tabsStore.selectTab({
                  ...bestTab,
                  partiallyOpen: false,
                })
                await sleep(16)
              }

              if (args.focus) {
                let preventScroll = args.focus.preventScroll

                if (
                  // sidebar focus is not implemented, so we'll skip the checks
                  args.focus.type !== 'block:sidebar'
                ) {
                  if (
                    document.activeElement instanceof HTMLElement &&
                    (document.activeElement.dataset.blockId ===
                      args.focus.blockId ||
                      document.activeElement.closest(
                        `[data-block-id="${args.focus.blockId}"]`
                      ))
                  ) {
                    // block is focused
                    if (args.focus.type === 'block:value') {
                      preventScroll = true
                    }
                  } else if (
                    repoStore.editorSelectedBlockId !== args.focus.blockId &&
                    (repoStore.editorSelectedBlockId ||
                      (!bestTab?.key.includes(args.focus.blockId) &&
                        !newTabKey.includes(args.focus.blockId)))
                  ) {
                    // not focused at all, replay
                    shouldReplay = true
                  }

                  if (args.focus.elementIdOverride) {
                    if (
                      document.activeElement?.id !==
                        args.focus.elementIdOverride &&
                      !document.activeElement?.closest(
                        `#${args.focus.elementIdOverride}`
                      )
                    ) {
                      shouldReplay = true
                    }
                  }
                }

                repoStore.setFocus({ ...args.focus, preventScroll })
              }

              if (shouldReplay && !isLastOp) {
                /**
                 * Interesting ux case.
                 *
                 * The user requested an undo/redo that's not in the active tab. We switch to it.
                 * But we don't want to keep going through the stack, as there would be too many things happening.
                 * Instead, we choose to stop the stack item here, and push this stack item again in the next index.
                 * As the user presses undo/redo again, we'll process this stack item again, isActiveTab will be true,
                 * and therefore we'll keep going through this stack.
                 *
                 * Another option would've been to include tab and focus changes to the undo/redo stack ala Figma, but
                 * that's way too complex and not actually a ux win in our case. Figma does that because selecting stuff
                 * in Figma may be a lot of work in some cases. Users sometimes "build the right selection" before taking
                 * action, and therefore it's great that Figma adds that to the stack.
                 */
                return 'should-replay'
              }

              await sleep(16)

              break
            }
            default:
              throw new Error(`op not implemented ${op}`)
          }
        }
      },
      [data?.replicache.mutate, repoStoreRoot, tabsStoreRoot, treeRef]
    )

  const processStackItemRef = React.useRef(processStackItem)
  // eslint-disable-next-line react-compiler/react-compiler
  processStackItemRef.current = processStackItem

  React.useEffect(() => {
    if (DISABLED) return
    // we want to ensure that the undo stack is unique per branch
    // and that it isn't enabled for commits
    if (!data?.repoId || !branchId) return

    undoManagerRef.current = createUndoManager({
      processStackItem: (...args) => processStackItemRef.current(...args),
      maxSize: 250,
    })
  }, [data?.repoId, branchId])

  React.useEffect(() => {
    if (DISABLED) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        e.target.closest('[data-native-undo-redo="true"]')
      ) {
        return
      }
      if (isHotkey('mod+z', e)) {
        e.preventDefault()
        undoManagerRef.current?.undo()
      } else if (isHotkey('mod+shift+z', e) || isHotkey('mod+y', e)) {
        e.preventDefault()
        undoManagerRef.current?.redo()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <RepoUndoManagerContext.Provider value={undoManagerRef}>
      {children}
    </RepoUndoManagerContext.Provider>
  )
}
